import { Box } from '@wix/design-system'
import { useContent } from '@wix/funnel-builder-lizard'
import React, { FC, MouseEventHandler, useEffect } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { createMetadataFromBusinessTerm } from '../../../category/categoryInMetadata'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { useNext } from '../../../hooks/useNext'
import { Button } from '../../reusable/Button'
import { LogoWithBack } from '../../reusable/LogoWithBack'
import { SkipButton } from '../../reusable/SkipButton'
import { getBIAppInstallation } from '../../WixMobile/WixMobileApps/common'
import { secondTryDataHooks as dataHooks } from './SecondTry.dataHooks'
import s from './SecondTry.scss'
import { useSecondTryContext } from './SecondTryContext/SecondTryContext'
import { TSecondTry as Content } from './typings/SecondTry.content'
import { Widget } from './Widget/Widget'

export const SecondTry: FC = () => {
  const { content } = useContent<Content>()
  const { skip, back, question, widget } = content
  const { categoryState, businessName, selectedAppDefIds, introData, recommendedApps } = useSecondTryContext()
  const next = useNext()
  const { save } = useStorageContext()
  const pageLogger = usePageLogger()

  useEffect(() => {
    void pageLogger.screenView()
  }, [])

  const handleBack = () => {
    void pageLogger.generalAction({ action: 'back' })
    window.history.back()
  }

  const handleSkip = () => {
    void pageLogger.generalAction({ action: 'skip' })
    next({ [question.questionKey]: { skipped: true } })
  }

  const handleOptionClick =
    (option: Content['question']['options'][number]): MouseEventHandler =>
    () => {
      if (option.isStartFromScratch) {
        next({ [question.questionKey]: { values: [option.optionKey] } })
        return
      }

      const { category } = introData
      const {
        businessTerm = category
          ? {
              displayName: category.displayName,
              industryId: category.industryId,
              structureId: category.structureId,
              segmentationIndustryId: category.segmentationIndustryId,
              businessType: category.businessType,
              postRegVertical: category.postRegVertical,
            }
          : {},
        businessSegmentId,
      } = categoryState.category ?? {}
      const { displayName } = businessTerm
      const metadata = createMetadataFromBusinessTerm(businessTerm, businessSegmentId)

      const { appId, builtInAppId } = getBIAppInstallation(recommendedApps, selectedAppDefIds)
      void pageLogger.appsSelected({ app_id: appId, built_in_app_id: builtInAppId })
      void pageLogger.answerCategory({ businessTerm, segment: metadata.segment, businessSegmentId })
      void pageLogger.answer({
        answer: option.optionKey,
        payload: {
          businessName,
          category: displayName,
          apps: selectedAppDefIds,
        },
      })

      // @andriipa: `save` is required in order to do setRecentIntroData with the proper values later
      save({
        businessName,
        category: categoryState.category ?? {
          search: category?.displayName || '',
          businessTerm,
          businessSegmentId,
          index: 0,
          searchSource: 'unknown',
        },
        appDefIds: selectedAppDefIds,
      })
      next(
        {
          [question.questionKey]: { values: [option.optionKey] },
          [content.widget.content.businessName.questionKey]: {
            values: businessName ? [businessName] : [],
          },
          [content.widget.content.category.questionKey]: {
            values: displayName ? [displayName] : [],
          },
          [content.widget.content.apps.questionKey]: {
            values: selectedAppDefIds,
          },
        },
        metadata,
      )
    }

  const title = introData.businessName
    ? question.content.title.replace('$businessName', introData.businessName)
    : question.content.fallbackTitle
  const hasActiveCategory = introData.category || categoryState.category

  return (
    <>
      <Box height="100vh" overflow="hidden">
        <LogoWithBack backButton={back} onBackClick={handleBack} />

        <Box width="55%" minWidth="calc(456px + 90px + 90px)" height="100%" overflow="hidden">
          <Box flex="1" className="fade-in-bottom">
            <Box direction="vertical" verticalAlign="middle" className={s.container}>
              <Box direction="vertical" gap="SP3">
                <h1 className={s.title} data-hook={dataHooks.title()}>
                  {title}
                </h1>
                <h2 className={s.subtitle} data-hook={dataHooks.subtitle()}>
                  {question.content.subtitle}
                </h2>
              </Box>
              <Box marginTop="SP7" gap="SP3">
                {question.options.map(option => (
                  <Button
                    onClick={handleOptionClick(option)}
                    key={option.optionKey}
                    label={option.title}
                    priority={option.isPrimary ? 'primary' : 'secondary'}
                    skin="standard"
                    id={option.optionKey}
                    disabled={!option.isStartFromScratch && !hasActiveCategory}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box flex="1" minWidth="608px" className="fade-in">
          <Box
            position="relative"
            width="100%"
            backgroundColor="rgba(37, 52, 119, 1)"
            align="center"
            verticalAlign="middle"
          >
            <Widget content={widget.content} />
          </Box>
        </Box>
        {skip && <SkipButton skin="light" skipButton={skip} onClick={handleSkip} dataHook={dataHooks.skip()} />}
      </Box>
    </>
  )
}
